import axios from 'axios';
// Note: file-saver import removed - using manual download implementation
import {
  Customer, Invoice, PurchaseOrder, PurchaseOrderDocument, Project, BillingRate, BillingRatePublic,
  UserRate, UserTimeEntry, WeeklySummary, POTimeEntriesSummary, POTimeEntriesDetail, PaginatedResponse,
  RateGroup, RateGroupAssignmentResponse, MonthlyTimesheet, MonthlyTimesheetDetail,
  CustomerTimesheetPreview, DateRangeType, UnbilledSummaryResponse, UnbilledEntriesResponse,
  UserProfile, UserProfileSummary, PayPeriod, PayPeriodGenerateRequest, PayPeriodGenerateResponse,
  BiweeklyTimesheet, BiweeklyTimesheetDetail, PayrollSettings,
  InternalTimeReportParams, InternalTimeReportPreview,
  MyTimeReportParams, MyTimeReportPreview, MyTimeReportProject,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Create axios instance first
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Required so backend session cookie is stored on OAuth URL request (QuickBooks connect flow)
});

// Add interceptor to include auth token in all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add interceptor to handle 401 errors (redirect to login)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Clear auth data
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const customerAPI = {
  getAll: () => api.get<PaginatedResponse<Customer>>('/customers/'),
  getById: (id: number) => api.get<Customer>(`/customers/${id}/`),
  create: (data: Customer) => api.post<Customer>('/customers/', data),
  update: (id: number, data: Customer) => api.put<Customer>(`/customers/${id}/`, data),
  delete: (id: number) => api.delete(`/customers/${id}/`),
};

export const purchaseOrderAPI = {
  getAll: (params?: { status?: PurchaseOrder['status'] }) =>
    api.get<PaginatedResponse<PurchaseOrder>>('/purchase-orders/', { params }),
  getById: (id: number) => api.get<PurchaseOrder>(`/purchase-orders/${id}/`),
  create: (data: PurchaseOrder) => api.post<PurchaseOrder>('/purchase-orders/', data),
  update: (id: number, data: PurchaseOrder) => api.put<PurchaseOrder>(`/purchase-orders/${id}/`, data),
  delete: (id: number) => api.delete(`/purchase-orders/${id}/`),
  getTimeEntriesSummary: (id: number, startDate: string, endDate: string) => 
    api.get<POTimeEntriesSummary>(`/purchase-orders/${id}/time-entries/?start_date=${startDate}&end_date=${endDate}`),
  getTimeEntriesDetail: (id: number, startDate: string, endDate: string) => 
    api.get<POTimeEntriesDetail>(`/purchase-orders/${id}/time-entries-detail/?start_date=${startDate}&end_date=${endDate}`),
};

export const purchaseOrderDocumentAPI = {
  getAll: (purchaseOrderId?: number) => {
    const params = purchaseOrderId ? { purchase_order: purchaseOrderId } : {};
    return api.get<PaginatedResponse<PurchaseOrderDocument>>('/po-documents/', { params });
  },
  getById: (id: number) => api.get<PurchaseOrderDocument>(`/po-documents/${id}/`),
  upload: (purchaseOrderId: number, file: File) => {
    const formData = new FormData();
    formData.append('purchase_order', purchaseOrderId.toString());
    formData.append('file', file);
    return api.post<PurchaseOrderDocument>('/po-documents/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  delete: (id: number) => api.delete(`/po-documents/${id}/`),
  download: (id: number) => {
    return api.get(`/po-documents/${id}/download/`, {
      responseType: 'blob',
    });
  },
};

export const invoiceAPI = {
  getAll: () => api.get<PaginatedResponse<Invoice>>('/invoices/'),
  getById: (id: number) => api.get<Invoice>(`/invoices/${id}/`),
  create: (data: Invoice) => api.post<Invoice>('/invoices/', data),
  update: (id: number, data: Invoice) => api.put<Invoice>(`/invoices/${id}/`, data),
  patch: (id: number, data: Partial<Invoice>) => api.patch<Invoice>(`/invoices/${id}/`, data),
  delete: (id: number) => api.delete(`/invoices/${id}/`),
  /** Export invoice as QuickBooks Desktop IIF file (download). */
  exportQuickbooksDesktop: (id: number) =>
    api.get(`/invoices/${id}/export-quickbooks-desktop/`, { responseType: 'blob' }),
  /** Export invoice to QuickBooks Online (returns 503 until QB is connected). */
  exportQuickbooksOnline: (id: number) =>
    api.post<{ message: string; quickbooks_customer_id?: string; quickbooks_invoice_id?: string }>(
      `/invoices/${id}/export-quickbooks-online/`
    ),
  clearQboLink: (id: number) =>
    api.post<{ message: string }>(`/invoices/${id}/clear-qbo-link/`),
};

export const quickbooksAPI = {
  getOAuthUrl: () =>
    api.get<{ authorization_url: string; redirect_uri: string }>('/quickbooks/oauth/url/'),
  getConnectionStatus: () =>
    api.get<{ connected: boolean; realm_id?: string }>('/quickbooks/connection/'),
  disconnect: () => api.post<{ message: string }>('/quickbooks/disconnect/'),
};

export const projectAPI = {
  getAll: (params?: any) => api.get<PaginatedResponse<Project>>('/projects/', { params }),
  getById: (id: number) => api.get<Project>(`/projects/${id}/`),
  create: (data: Partial<Project>) => api.post<Project>('/projects/', data),
  update: (id: number, data: Partial<Project>) => api.put<Project>(`/projects/${id}/`, data),
  delete: (id: number) => api.delete(`/projects/${id}/`),
};

export const billingRateAPI = {
  getAll: (params?: any) => api.get<PaginatedResponse<BillingRate>>('/billing-rates/', { params }),
  getById: (id: number) => api.get<BillingRate>(`/billing-rates/${id}/`),
  create: (data: Partial<BillingRate>) => api.post<BillingRate>('/billing-rates/', data),
  update: (id: number, data: Partial<BillingRate>) => api.put<BillingRate>(`/billing-rates/${id}/`, data),
  delete: (id: number) => api.delete(`/billing-rates/${id}/`),
  getMyRates: (userId?: number) => {
    const params = userId ? { user_id: userId } : {};
    return api.get<BillingRatePublic[]>('/billing-rates/my_rates/', { params });
  },
};

export interface UserWithRateCount {
  id: number;
  username: string;
  full_name: string;
  rates_count: number;
}

export const userRateAPI = {
  getAll: (params?: any) => {
    return api.get<PaginatedResponse<UserRate>>('/user-rates/', { params });
  },
  getById: (id: number) => api.get<UserRate>(`/user-rates/${id}/`),
  create: (data: Partial<UserRate>) => api.post<UserRate>('/user-rates/', data),
  update: (id: number, data: Partial<UserRate>) => api.put<UserRate>(`/user-rates/${id}/`, data),
  delete: (id: number) => api.delete(`/user-rates/${id}/`),
  getUsersSummary: (params?: { page?: number; page_size?: number }) =>
    api.get<PaginatedResponse<UserWithRateCount>>('/user-rates/users-summary/', { params }),
};

export const timeEntryAPI = {
  getAll: (params?: { start_date?: string; end_date?: string; week_of?: string }) => 
    api.get<PaginatedResponse<UserTimeEntry>>('/time-entries/', { params }),
  getById: (id: number) => api.get<UserTimeEntry>(`/time-entries/${id}/`),
  create: (data: Partial<UserTimeEntry>) => api.post<UserTimeEntry>('/time-entries/', data),
  update: (id: number, data: Partial<UserTimeEntry>) => api.put<UserTimeEntry>(`/time-entries/${id}/`, data),
  delete: (id: number) => api.delete(`/time-entries/${id}/`),
  getWeeklySummary: (weekOf: string) => 
    api.get<WeeklySummary>('/time-entries/weekly_summary/', { params: { week_of: weekOf } }),
  writeOff: (id: number, reason?: string) => 
    api.post<{ message: string; entry: UserTimeEntry }>(`/time-entries/${id}/write_off/`, { reason }),
  undoWriteOff: (id: number) => 
    api.post<{ message: string; entry: UserTimeEntry }>(`/time-entries/${id}/undo_write_off/`),
  bulkWriteOff: (entryIds: number[], reason?: string) => 
    api.post<{ message: string; written_off_count: number; skipped_count: number; skipped_ids: number[] }>(
      '/time-entries/bulk_write_off/', 
      { entry_ids: entryIds, reason }
    ),
};

export interface UnbilledEntriesParams {
  customer_id?: number;
  project_id?: number;
  user_id?: number;
  start_date?: string;
  end_date?: string;
  group_by?: 'customer' | 'project';
}

export const unbilledTimeAPI = {
  getSummary: () => api.get<UnbilledSummaryResponse>('/unbilled-time/summary/'),
  getEntries: (params?: UnbilledEntriesParams) => 
    api.get<UnbilledEntriesResponse>('/unbilled-time/entries/', { params }),
};

export const rateGroupAPI = {
  getAll: () => api.get<PaginatedResponse<RateGroup>>('/rate-groups/'),
  getById: (id: number) => api.get<RateGroup>(`/rate-groups/${id}/`),
  create: (data: Partial<RateGroup>) => api.post<RateGroup>('/rate-groups/', data),
  update: (id: number, data: Partial<RateGroup>) => api.put<RateGroup>(`/rate-groups/${id}/`, data),
  delete: (id: number) => api.delete(`/rate-groups/${id}/`),
  assignToUser: (id: number, userId: number) => 
    api.post<RateGroupAssignmentResponse>(`/rate-groups/${id}/assign-to-user/`, { user_id: userId }),
};

export const monthlyTimesheetAPI = {
  getAll: (params?: { user_id?: number; year?: number; month?: number; status?: string }) =>
    api.get<PaginatedResponse<MonthlyTimesheet>>('/monthly-timesheets/', { params }),
  getById: (id: number) => api.get<MonthlyTimesheetDetail>(`/monthly-timesheets/${id}/`),
  getForMonth: (year: number, month: number, userId?: number) => {
    const params: any = { year, month };
    if (userId) params.user_id = userId;
    return api.get<MonthlyTimesheetDetail>('/monthly-timesheets/for_month/', { params });
  },
  submit: (id: number) => 
    api.post(`/monthly-timesheets/${id}/submit/`),
  approve: (id: number) => 
    api.post(`/monthly-timesheets/${id}/approve/`),
  reject: (id: number, reason: string) => 
    api.post(`/monthly-timesheets/${id}/reject/`, { reason }),
  getPendingApprovals: (params?: { user_id?: number; year?: number; month?: number }) =>
    api.get<PaginatedResponse<MonthlyTimesheet>>('/monthly-timesheets/pending_approvals/', { params }),
};

export interface CustomerTimesheetParams {
  /** When scoping by company */
  customer_id?: number;
  /** When scoping by purchase order */
  purchase_order_id?: number;
  date_range_type: DateRangeType;
  start_date?: string;
  end_date?: string;
}

export const customerTimesheetAPI = {
  preview: (params: CustomerTimesheetParams) =>
    api.get<CustomerTimesheetPreview>('/customer-timesheets/preview/', { params }),
  
  export: async (params: CustomerTimesheetParams): Promise<void> => {
    console.log('Starting export with params:', params);

    const requestParams: Record<string, string | number | undefined> = {
      date_range_type: params.date_range_type,
      start_date: params.start_date,
      end_date: params.end_date,
    };
    if (params.purchase_order_id != null) {
      requestParams.purchase_order_id = params.purchase_order_id;
    } else if (params.customer_id != null) {
      requestParams.customer_id = params.customer_id;
    }

    const response = await api.get('/customer-timesheets/export/', {
      params: requestParams,
      responseType: 'blob',
    });
    
    console.log('Response received, status:', response.status);
    
    // Extract filename from Content-Disposition header if available
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'timesheet.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    console.log('Using filename:', filename);
    
    // Create blob with proper type
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    console.log('Blob created, size:', blob.size);
    
    // Use FileReader to convert to base64 data URL
    // This approach works more reliably in some browser configurations
    const reader = new FileReader();
    
    reader.onload = function() {
      const dataUrl = reader.result as string;
      console.log('Data URL created, length:', dataUrl.length);
      
      // Create anchor with data URL
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = filename;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('Download triggered');
    };
    
    reader.onerror = function() {
      console.error('FileReader error:', reader.error);
    };
    
    reader.readAsDataURL(blob);
  },
  
  // Alternative export that opens in a new window (for debugging)
  exportDirect: (params: CustomerTimesheetParams): void => {
    const queryParams = new URLSearchParams();
    if (params.purchase_order_id != null) {
      queryParams.append('purchase_order_id', params.purchase_order_id.toString());
    } else if (params.customer_id != null) {
      queryParams.append('customer_id', params.customer_id.toString());
    }
    queryParams.append('date_range_type', params.date_range_type);
    if (params.start_date) {
      queryParams.append('start_date', params.start_date);
    }
    if (params.end_date) {
      queryParams.append('end_date', params.end_date);
    }
    
    // Get the auth token and add to URL (for direct download)
    const token = localStorage.getItem('access_token');
    if (token) {
      queryParams.append('token', token);
    }
    
    // Open in new window - browser will handle the download
    window.open(`${API_BASE_URL}/customer-timesheets/export/?${queryParams.toString()}`, '_blank');
  },
};

// User Profile API (Employee Type Management)
export const userProfileAPI = {
  getAll: (params?: { employee_type?: string }) =>
    api.get<PaginatedResponse<UserProfile>>('/user-profiles/', { params }),
  getById: (id: number) => api.get<UserProfile>(`/user-profiles/${id}/`),
  update: (id: number, data: Partial<UserProfile>) => 
    api.patch<UserProfile>(`/user-profiles/${id}/`, data),
  getSummary: () => api.get<UserProfileSummary>('/user-profiles/summary/'),
  getMyProfile: () => api.get<UserProfile>('/user-profiles/me/'),
};

// Pay Period API
export const payPeriodAPI = {
  getAll: (params?: { is_locked?: boolean; start_date?: string; end_date?: string; page_size?: number }) =>
    api.get<PaginatedResponse<PayPeriod>>('/pay-periods/', { params }),
  getById: (id: number) => api.get<PayPeriod>(`/pay-periods/${id}/`),
  create: (data: Partial<PayPeriod>) => api.post<PayPeriod>('/pay-periods/', data),
  update: (id: number, data: Partial<PayPeriod>) => api.put<PayPeriod>(`/pay-periods/${id}/`, data),
  delete: (id: number) => api.delete(`/pay-periods/${id}/`),
  getCurrent: () => api.get<PayPeriod>('/pay-periods/current/'),
  generate: (data: PayPeriodGenerateRequest) => 
    api.post<PayPeriodGenerateResponse>('/pay-periods/generate/', data),
  lock: (id: number) => api.post<{ message: string; period: PayPeriod }>(`/pay-periods/${id}/lock/`),
  unlock: (id: number) => api.post<{ message: string; period: PayPeriod }>(`/pay-periods/${id}/unlock/`),
};

// Payroll Settings API (for auto-generating pay periods)
export const payrollSettingsAPI = {
  get: () => api.get<PayrollSettings>('/payroll-settings/settings/'),
  update: (data: Partial<PayrollSettings>) => 
    api.patch<PayrollSettings>('/payroll-settings/settings/', data),
  generatePeriods: (endDate?: string) => 
    api.post<PayPeriodGenerateResponse>('/payroll-settings/generate_periods/', 
      endDate ? { end_date: endDate } : {}),
};

// Biweekly Timesheet API
export const biweeklyTimesheetAPI = {
  getAll: (params?: { user_id?: number; status?: string; pay_period?: number }) =>
    api.get<PaginatedResponse<BiweeklyTimesheet>>('/biweekly-timesheets/', { params }),
  getById: (id: number) => api.get<BiweeklyTimesheetDetail>(`/biweekly-timesheets/${id}/`),
  getForPeriod: (payPeriodId: number, userId?: number) => {
    const params: any = { pay_period_id: payPeriodId };
    if (userId) params.user_id = userId;
    return api.get<BiweeklyTimesheetDetail>('/biweekly-timesheets/for_period/', { params });
  },
  submit: (id: number) => 
    api.post(`/biweekly-timesheets/${id}/submit/`),
  approve: (id: number) => 
    api.post(`/biweekly-timesheets/${id}/approve/`),
  reject: (id: number, reason: string) => 
    api.post(`/biweekly-timesheets/${id}/reject/`, { reason }),
  unapprove: (id: number) => 
    api.post(`/biweekly-timesheets/${id}/unapprove/`),
  getPendingApprovals: (params?: { user_id?: number; pay_period?: number }) =>
    api.get<BiweeklyTimesheet[]>('/biweekly-timesheets/pending_approvals/', { params }),
};

// Internal Time Report API
export const internalTimeReportAPI = {
  preview: (params: InternalTimeReportParams) =>
    api.get<InternalTimeReportPreview>('/internal-time-report/preview/', { params }),

  export: async (params: InternalTimeReportParams): Promise<void> => {
    const response = await api.get('/internal-time-report/export/', {
      params,
      responseType: 'blob',
    });

    const contentDisposition = response.headers['content-disposition'];
    let filename = 'time_report.xlsx';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match?.[1]) {
        filename = match[1].replace(/['"]/g, '');
      }
    }

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const reader = new FileReader();
    reader.onload = () => {
      const link = document.createElement('a');
      link.href = reader.result as string;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
    reader.readAsDataURL(blob);
  },
};

// My Time Report API (own time; admins may pass user_id)
export const myTimeReportAPI = {
  preview: (params: MyTimeReportParams) =>
    api.get<MyTimeReportPreview>('/my-time-report/preview/', { params }),
  projects: (params?: { user_id?: number }) =>
    api.get<MyTimeReportProject[]>('/my-time-report/projects/', { params }),
};

export default api;
